/**
 * Billing API client for the billing dashboard.
 */

const API_URL = import.meta.env.VITE_API_URL

export interface SubscriptionDetails {
  tier: number | null
  billing_frequency: string
  payment_type: string
  billing_amount: number | null
  subscription_status: string
  next_billing_date: string | null
  membership_until: string | null
  // If set, subscription is pending cancellation
  cancellation_date: string | null
}

export interface BillingInfo {
  billing_email: string | null
  billing_legal_name: string | null
  billing_contact_name: string | null
  billing_contact_phone: string | null
  billing_address_line1: string | null
  billing_address_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postal_code: string | null
  billing_country: string | null
  tax_registration_number: string | null
}

export interface ActiveDiscount {
  code: string
  discount_type: 'percentage'
  discount_value: number
  expires_at: string | null
  days_remaining: number | null
}

export interface RecentPayment {
  id: number
  platform: string
  amount: number
  type: string
  created_at: string
  reference: string | null
}

export interface PendingBillingTransition {
  transition_id?: string | null
  target_payment_type: 'invoice'
  target_billing_frequency: string
  effective_at: string
}

export interface BillingDashboardResponse {
  subscription: SubscriptionDetails
  billing_info: BillingInfo
  discounts: ActiveDiscount[]
  recent_payments: RecentPayment[]
  billing_sync_status?: string | null  // synced, pending_sync, sync_error
  has_pending_payment_transition?: boolean
  pending_payment_transition?: PendingBillingTransition | null
}

/**
 * Fetch billing dashboard data for an organization.
 */
export async function fetchBillingDashboard(
  orgUUID: string,
  token: string
): Promise<BillingDashboardResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/dashboard?org_uuid=${orgUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to fetch billing dashboard: ${response.status}`)
  }

  return response.json()
}

export interface PaymentHistoryItem {
  id: number
  platform: string
  amount: number
  type: string
  created_at: string
  external_reference: string | null
}

export interface PaymentHistoryResponse {
  items: PaymentHistoryItem[]
  total: number
  limit: number
  offset: number
}

export interface PaymentHistoryParams {
  orgUUID: string
  token: string
  limit?: number
  offset?: number
  platform?: string | null
  startDate?: string | null
  endDate?: string | null
}

/**
 * Fetch paginated payment history for an organization.
 */
export async function fetchPaymentHistory(
  params: PaymentHistoryParams
): Promise<PaymentHistoryResponse> {
  const { orgUUID, token, limit = 10, offset = 0, platform, startDate, endDate } = params

  const queryParams = new URLSearchParams({
    org_uuid: orgUUID,
    limit: limit.toString(),
    offset: offset.toString(),
  })

  if (platform) {
    queryParams.append('platform', platform)
  }
  if (startDate) {
    queryParams.append('start_date', startDate)
  }
  if (endDate) {
    queryParams.append('end_date', endDate)
  }

  const response = await fetch(`${API_URL}/subscriptions/history?${queryParams}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to fetch payment history: ${response.status}`)
  }

  return response.json()
}

/**
 * Download invoice PDF for a charge.
 * Opens in new tab for Stripe (redirect), downloads for Xero (blob).
 */
export async function downloadInvoice(
  chargeId: number,
  orgUUID: string,
  token: string
): Promise<void> {
  const url = `${API_URL}/subscriptions/invoice/${chargeId}?org_uuid=${orgUUID}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to download invoice: ${response.status}`)
  }

  // Check if it's a redirect (Stripe) or PDF content (Xero)
  const contentType = response.headers.get('content-type')

  if (contentType?.includes('application/pdf')) {
    // Xero - download the PDF blob
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `invoice-${chargeId}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } else {
    // Stripe redirect - open in new tab
    window.open(response.url, '_blank')
  }
}

/**
 * Update billing information for an organization.
 * All fields are optional - only provided fields will be updated.
 */
export async function updateBillingInfo(
  orgUUID: string,
  token: string,
  billingInfo: Partial<BillingInfo>
): Promise<BillingInfo> {
  const response = await fetch(
    `${API_URL}/subscriptions/billing-info?org_uuid=${orgUUID}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(billingInfo),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to update billing info: ${response.status}`)
  }

  return response.json()
}

export interface ChangeTierResponse {
  success: boolean
  message: string
  old_tier: number | null
  new_tier: number
  new_billing_amount: number | null
  effective_date: string | null
}

/**
 * Change subscription tier for an organization.
 * Upgrades take effect immediately; downgrades take effect at period end.
 */
export async function changeTier(
  orgUUID: string,
  token: string,
  newTier: number
): Promise<ChangeTierResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/change-tier?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ new_tier: newTier }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to change tier: ${response.status}`)
  }

  return response.json()
}

export interface ChangeFrequencyResponse {
  success: boolean
  message: string
  old_frequency: string | null
  new_frequency: string
  new_billing_amount: number | null
  effective_date: string | null
  proration_preview: ProrationPreview | null
}

export class ChangeFrequencyError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ChangeFrequencyError'
    this.status = status
  }
}

/**
 * Map a ChangeFrequencyError to a user-facing message string.
 * String-match on status and message; no backend changes needed.
 */
export function changeFrequencyErrorMessage(error: ChangeFrequencyError): string {
  const msg = error.message?.toLowerCase() ?? ''
  if (error.status === 400 && msg.includes('already on')) {
    return "You're already on that plan."
  }
  if (error.status === 500 && msg.includes('not configured')) {
    return "This plan isn't available right now. Please contact support."
  }
  if (
    error.status === 500 &&
    (msg.includes('multiple recurring') || msg.includes('unexpected structure'))
  ) {
    return 'Your subscription needs manual review. Please contact support.'
  }
  if (error.status === 502) {
    // Surface the Stripe detail directly
    return error.message
  }
  if (error.status === 503) {
    return "Couldn't preview your proration. Please try again."
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Change billing frequency for an organization.
 */
export async function changeFrequency(
  orgUUID: string,
  token: string,
  newFrequency: string
): Promise<ChangeFrequencyResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/change-frequency?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ new_frequency: newFrequency }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message = errorData.detail || `Failed to change frequency: ${response.status}`
    throw new ChangeFrequencyError(message, response.status)
  }

  return response.json()
}

export interface PreviewFrequencyResponse {
  current_frequency: string
  new_frequency: string
  proration_preview: ProrationPreview
}

export class PreviewFrequencyError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'PreviewFrequencyError'
    this.status = status
  }
}

/**
 * Map a PreviewFrequencyError to a user-facing message string.
 */
export function previewFrequencyErrorMessage(error: PreviewFrequencyError): string {
  if (error.status === 503) {
    return "Couldn't preview your proration. Please try again."
  }
  if (error.status === 500) {
    const msg = error.message?.toLowerCase() ?? ''
    if (msg.includes('manual review')) {
      return 'Your subscription needs manual review. Please contact support.'
    }
    return "This plan isn't available right now. Please contact support."
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Preview the proration for a frequency swap (read-only, does not modify subscription).
 */
export async function previewFrequency(
  orgUUID: string,
  token: string,
  newFrequency: string
): Promise<PreviewFrequencyResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/preview-frequency?org_uuid=${orgUUID}&new_frequency=${encodeURIComponent(newFrequency)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message = errorData.detail || `Failed to preview frequency: ${response.status}`
    throw new PreviewFrequencyError(message, response.status)
  }

  return response.json()
}

export interface CancelSubscriptionResponse {
  success: boolean
  message: string
  cancelled_at: string | null
  effective_date: string | null
  subscription_status: string
}

/**
 * Cancel subscription for an organization.
 * For Stripe: cancel immediately or at period end.
 * For Invoice: stops future invoices.
 */
export async function cancelSubscription(
  orgUUID: string,
  token: string,
  cancelImmediately: boolean,
  reason?: string
): Promise<CancelSubscriptionResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/cancel?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        cancel_immediately: cancelImmediately,
        reason: reason || null,
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to cancel subscription: ${response.status}`)
  }

  return response.json()
}

export interface ReactivateSubscriptionResponse {
  success: boolean
  message: string
  reactivated_at?: string
  subscription_status: string
  // For fully terminated subscriptions that need a new checkout
  requires_new_subscription?: boolean
  checkout_client_secret?: string
}

/**
 * Reactivate a cancelled subscription.
 * For Stripe: removes cancel_at_period_end flag.
 * For Invoice: creates new Xero repeating invoice.
 */
export async function reactivateSubscription(
  orgUUID: string,
  token: string
): Promise<ReactivateSubscriptionResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/reactivate?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to reactivate subscription: ${response.status}`)
  }

  return response.json()
}

export interface SwitchPaymentTypeResponse {
  success: boolean
  message: string
  old_payment_type: string
  new_payment_type: string
  effective_date: string | null
  // For Invoice -> Stripe, may need Stripe checkout
  requires_checkout?: boolean
  checkout_client_secret?: string
  // Proration info for Invoice -> Stripe
  invoice_status?: string | null // "paid" or "unpaid"
  outstanding_amount?: number | null // inc GST, if unpaid
  billing_starts?: string | null // when Stripe billing begins
}

export interface ProrationPreview {
  amount_due: number | null
  currency: string | null
  due_date: string | null
}

export interface SwitchPreflightResponse {
  can_switch: boolean
  current_payment_type: string
  invoice_status: 'paid' | 'unpaid' | null
  outstanding_amount: number | null // inc GST, if unpaid
  paid_until: string | null // date string, if paid
  days_remaining: number | null // if paid
  monthly_stripe_amount: number | null // inc GST
  quarterly_stripe_amount: number | null // inc GST
  annual_stripe_amount: number | null // inc GST
  stripe_eligible: boolean
  proration_preview: ProrationPreview | null
}

/**
 * Preflight check before switching payment type.
 * Returns information about invoice status and proration for the switch dialog.
 */
export async function getSwitchPreflight(
  orgUUID: string,
  token: string
): Promise<SwitchPreflightResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/switch-preflight?org_uuid=${orgUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to get switch preflight: ${response.status}`)
  }

  return response.json()
}

/**
 * Switch payment type between Stripe and Invoice.
 * Stripe -> Invoice: Stripe cancels at period end, Xero invoice set up.
 * Invoice -> Stripe: Returns checkout for new subscription at the selected frequency.
 */
export async function switchPaymentType(
  orgUUID: string,
  token: string,
  newPaymentType: 'stripe' | 'invoice',
  billingFrequency?: string
): Promise<SwitchPaymentTypeResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/switch-payment-type?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        new_payment_type: newPaymentType,
        billing_frequency: billingFrequency || null,
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to switch payment type: ${response.status}`)
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// Unified change-billing endpoint (payment type + frequency in one request)
// ---------------------------------------------------------------------------

export type ChangeBillingRoute =
  | 'noop'
  | 'frequency_change'
  | 'switch_to_stripe'
  | 'switch_to_invoice'
  | 'revert_pending'

export interface ChangeBillingResponse {
  success: boolean
  no_change: boolean
  already_pending: boolean
  route_taken: ChangeBillingRoute
  message: string
  old_payment_type: string | null
  new_payment_type: string
  old_frequency: string | null
  new_frequency: string
  effective_date: string | null
  new_billing_amount: number | null
  proration_preview: ProrationPreview | null
  pending_transition: Record<string, unknown> | null
  // Invoice -> Stripe only
  requires_checkout: boolean
  checkout_client_secret: string | null
  invoice_status: string | null
  outstanding_amount: number | null
  billing_starts: string | null
}

// Stripe → Invoice only: when the first invoice is issued.
export type InvoiceStart = 'now' | 'period_end'

export interface ChangeBillingPreviewResponse {
  route: ChangeBillingRoute
  current_payment_type: string
  current_frequency: string | null
  target_payment_type: string
  target_frequency: string
  effective_date: string | null
  new_billing_amount: number | null
  proration_preview: ProrationPreview | null
  pending_transition: Record<string, unknown> | null
  already_pending: boolean
  default_invoice_start: InvoiceStart | null
  // First-invoice date (YYYY-MM-DD) for each start; period_end is null when
  // no Stripe period is still running.
  first_invoice_dates: { now: string; period_end: string | null } | null
  invoice_status: string | null
  outstanding_amount: number | null
  billing_starts: string | null
}

export class ChangeBillingError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ChangeBillingError'
    this.status = status
  }
}

export interface CancelPendingTransitionResponse {
  success: boolean
  message: string
}

/**
 * Map a ChangeBillingError to a user-facing message string.
 * The backend's 400 detail for the monthly+invoice rule is already friendly,
 * so it is surfaced verbatim.
 */
export function changeBillingErrorMessage(error: ChangeBillingError): string {
  const msg = error.message?.toLowerCase() ?? ''
  if (error.status === 400) {
    // Surface the backend detail directly (e.g. the monthly+invoice rule).
    return error.message
  }
  if (error.status === 404) {
    return 'We could not find your organisation. Please refresh and try again.'
  }
  if (error.status === 409) {
    // A scheduled or in-progress billing change; the detail says which.
    return error.message
  }
  if (
    error.status === 500 &&
    (msg.includes('multiple recurring') || msg.includes('unexpected'))
  ) {
    return 'Your subscription needs manual review. Please contact support.'
  }
  if (error.status === 502) {
    // Surface the Stripe detail directly.
    return error.message
  }
  if (error.status === 503) {
    return "Couldn't preview your proration. Please try again."
  }
  return 'Something went wrong. Please try again.'
}

/**
 * Preview a unified change-billing request (read-only — no Stripe/Xero/DB writes).
 */
export async function previewChangeBilling(
  orgUUID: string,
  token: string,
  paymentType: 'stripe' | 'invoice',
  billingFrequency: string
): Promise<ChangeBillingPreviewResponse> {
  const query = new URLSearchParams({
    org_uuid: orgUUID,
    payment_type: paymentType,
    billing_frequency: billingFrequency,
  })
  const response = await fetch(
    `${API_URL}/subscriptions/change-billing/preview?${query}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message =
      errorData.detail || `Failed to preview billing change: ${response.status}`
    throw new ChangeBillingError(
      typeof message === 'string' ? message : 'Invalid billing combination',
      response.status
    )
  }

  return response.json()
}

/**
 * Submit a unified change-billing request — changes payment type and/or
 * billing frequency in a single atomic call.
 */
export async function changeBilling(
  orgUUID: string,
  token: string,
  paymentType: 'stripe' | 'invoice',
  billingFrequency: string,
  invoiceStart?: InvoiceStart
): Promise<ChangeBillingResponse> {
  const response = await fetch(
    `${API_URL}/subscriptions/change-billing?org_uuid=${orgUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        payment_type: paymentType,
        billing_frequency: billingFrequency,
        ...(invoiceStart ? { invoice_start: invoiceStart } : {}),
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message =
      errorData.detail || `Failed to change billing: ${response.status}`
    throw new ChangeBillingError(
      typeof message === 'string' ? message : 'Invalid billing combination',
      response.status
    )
  }

  return response.json()
}

export async function cancelPendingBillingTransition(
  orgUUID: string,
  token: string,
  expectedTransitionId: string
): Promise<CancelPendingTransitionResponse> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 30_000)
  try {
    const response = await fetch(
      `${API_URL}/subscriptions/change-billing/cancel-pending?org_uuid=${orgUUID}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ expected_transition_id: expectedTransitionId }),
        signal: controller.signal,
      }
    )
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const message =
        errorData.detail || `Failed to cancel scheduled billing change: ${response.status}`
      throw new ChangeBillingError(
        typeof message === 'string' ? message : 'Failed to cancel scheduled billing change',
        response.status
      )
    }
    return response.json()
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ChangeBillingError('The request timed out. Please try again.', 408)
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}
